import React from "react";
import { Bell } from "lucide-react";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import { getPushSubscriptionState, pushSupported, subscribeToPush, unsubscribeFromPush } from "../../lib/webPush";

export default function NotificationSettings({ dancerId }) {
  const [state, setState] = React.useState({ supported: false, subscribed: false, loading: true });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await getPushSubscriptionState();
      if (!cancelled) setState({ ...next, loading: false });
    })();
    return () => { cancelled = true; };
  }, []);

  if (!pushSupported()) return null;
  if (state.loading || state.subscribed) return null;

  async function handleToggle() {
    if (!dancerId || busy) return;
    setBusy(true);
    try {
      if (state.subscribed) {
        await unsubscribeFromPush(dancerId);
        setState((s) => ({ ...s, subscribed: false }));
      } else {
        const result = await subscribeToPush(dancerId);
        if (result.ok) setState((s) => ({ ...s, subscribed: true }));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReadableCard className="p-4" data-testid="notification-settings">
      <div className="flex items-start gap-3">
        <div className="rounded-md2 bg-cream p-2 shrink-0" aria-hidden>
          <Bell className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-ui font-semibold text-[15px]">Push notifications</p>
          <p className="font-ui font-medium text-[13px] text-ink/70 mt-0.5">
            Schedule changes, host messages, and RSVP reminders for events you follow.
          </p>
          <ReadableNavButton
            type="button"
            variant={state.subscribed ? "default" : "primary"}
            className="mt-3 min-h-[44px] px-4 text-[14px]"
            disabled={busy || state.loading}
            onClick={handleToggle}
            data-testid="push-notification-toggle"
          >
            {busy ? "Saving…" : state.subscribed ? "Turn off push" : "Enable push"}
          </ReadableNavButton>
        </div>
      </div>
    </ReadableCard>
  );
}
